/**
 * 分页列表 Hook —— 列表页统一的分页/筛选/排序/降级逻辑
 *
 * 与 useResource 同样的降级策略：后端未实现时用演示数据，并在页面标注。
 */

import { useCallback, useMemo, useState } from 'react';
import type { TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { ApiCode, type PageResult } from '@ilc/shared';
import { ApiError } from '@/api/client';
import { useResource } from './useResource';

export interface PagedListResult<T, Q> {
  list: T[];
  total: number;
  loading: boolean;
  error: ApiError | null;
  isFallback: boolean;
  fallbackReason: string | null;
  query: Q;
  /** 局部更新查询条件；除 page 外任何改动都会回到第 1 页 */
  setQuery: (patch: Partial<Q>) => void;
  resetQuery: () => void;
  reload: () => void;
  pagination: TablePaginationConfig;
  /** 直接挂到 antd Table 的 onChange */
  handleTableChange: (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<T> | SorterResult<T>[],
  ) => void;
  setList: React.Dispatch<React.SetStateAction<T[]>>;
}

export interface UsePagedListOptions<T, Q extends { page?: number; pageSize?: number }> {
  /** 真实接口 */
  loader: (query: Q) => Promise<PageResult<T>>;
  /** 演示数据（待后端接口就绪后移除） */
  fallback?: (query: Q) => PageResult<T>;
  initialQuery: Q;
  /** 启用的查询条件键，用于 resetQuery */
  pageSize?: number;
  enabled?: boolean;
}

export function usePagedList<T, Q extends { page?: number; pageSize?: number }>(
  options: UsePagedListOptions<T, Q>,
): PagedListResult<T, Q> {
  const { loader, fallback, initialQuery, pageSize: defaultPageSize = 20, enabled = true } = options;

  const [query, setQueryState] = useState<Q>({
    page: 1,
    pageSize: defaultPageSize,
    ...initialQuery,
  });

  const resource = useResource<PageResult<T>>(() => loader(query), {
    fallback: fallback ? () => fallback(query) : undefined,
    deps: [JSON.stringify(query)],
    enabled,
  });

  const setQuery = useCallback((patch: Partial<Q>) => {
    setQueryState((prev) => {
      const next = { ...prev, ...patch };
      // 翻页以外的条件变化都要回到第一页，否则会出现「第 3 页空白」
      if (!('page' in patch)) next.page = 1;
      return next;
    });
  }, []);

  const resetQuery = useCallback(() => {
    setQueryState({ page: 1, pageSize: defaultPageSize, ...initialQuery });
  }, [initialQuery, defaultPageSize]);

  const list = resource.data?.list ?? [];
  const total = resource.data?.total ?? 0;

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: query.page ?? 1,
      pageSize: query.pageSize ?? defaultPageSize,
      total,
      showSizeChanger: true,
      showQuickJumper: true,
      pageSizeOptions: ['10', '20', '50', '100'],
      size: 'small',
      showTotal: (t: number, range: [number, number]) =>
        `第 ${range[0]}-${range[1]} 条 / 共 ${t} 条`,
    }),
    [query.page, query.pageSize, total, defaultPageSize],
  );

  const handleTableChange = useCallback<PagedListResult<T, Q>['handleTableChange']>(
    (pager, _filters, sorter) => {
      const single = Array.isArray(sorter) ? sorter[0] : sorter;
      const sortField = single?.field ? String(single.field) : undefined;
      const sortOrder =
        single?.order === 'ascend' ? 'asc' : single?.order === 'descend' ? 'desc' : undefined;

      setQueryState((prev) => ({
        ...prev,
        page: pager.current ?? 1,
        pageSize: pager.pageSize ?? prev.pageSize,
        ...(sortField && sortOrder
          ? { sortBy: sortField, sortOrder }
          : { sortBy: undefined, sortOrder: undefined }),
      }) as Q);
    },
    [],
  );

  const setList = useCallback<React.Dispatch<React.SetStateAction<T[]>>>(
    (updater) => {
      resource.setData((prev) => {
        const currentList = prev?.list ?? [];
        const nextList =
          typeof updater === 'function'
            ? (updater as (l: T[]) => T[])(currentList)
            : updater;
        const base: PageResult<T> = prev ?? {
          list: [],
          total: 0,
          page: query.page ?? 1,
          pageSize: query.pageSize ?? defaultPageSize,
        };
        return { ...base, list: nextList, total: Math.max(base.total, nextList.length) };
      });
    },
    [resource, query.page, query.pageSize, defaultPageSize],
  );

  return {
    list,
    total,
    loading: resource.loading,
    error: resource.error,
    isFallback: resource.isFallback,
    fallbackReason: resource.fallbackReason,
    query,
    setQuery,
    resetQuery,
    reload: resource.reload,
    pagination,
    handleTableChange,
    setList,
  };
}

export { ApiCode };
export default usePagedList;
